import { ArrowRight, Check, ShoppingCart } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { HTMLMotionProps } from 'framer-motion';

import { cn } from '@/shared/utils/cn';

import type { CourseCardCartState } from '../types/course-card';

interface CourseCardActionButtonProps extends HTMLMotionProps<'button'> {
  cartState: CourseCardCartState;
  compact?: boolean;
  /** El CTA de acreditaciones nombra la acción ("Certificarme"), sin ícono de carrito. */
  hideIdleIcon?: boolean;
  idleLabel?: string;
  onCartFlashAnimationComplete?: () => void;
  payLabel?: string;
}

export function CourseCardActionButton({
  cartState,
  compact: _compact,
  hideIdleIcon = false,
  idleLabel = 'Inscribirme',
  onCartFlashAnimationComplete,
  payLabel = 'Ir a pagar',
  className,
  ...props
}: CourseCardActionButtonProps) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.button
      className={cn(
        'focus-visible:ring-(--color-primary-500) relative isolate inline-flex h-11 w-full min-w-0 cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-lg border px-3 text-[0.82rem] font-semibold transition-[background-color,border-color,color,box-shadow] duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none',
        cartState === 'flash' &&
          'border-(--brand-secondary) bg-(--brand-secondary) text-adipa-primary-foreground hover:bg-(--brand-secondary)',
        cartState === 'pay' &&
          'border-(--brand-secondary) bg-(--brand-secondary) text-adipa-primary-foreground hover:border-[color-mix(in_srgb,var(--brand-secondary)_88%,var(--brand-navy))] hover:bg-[color-mix(in_srgb,var(--brand-secondary)_88%,var(--brand-navy))] hover:shadow-[0_10px_22px_rgba(44,183,255,0.18)]',
        cartState === 'idle' &&
          'border-(--color-primary-500) bg-(--color-primary-500) text-adipa-primary-foreground hover:bg-(--color-primary-600) hover:border-(--color-primary-600) hover:shadow-[0_10px_22px_rgba(112,78,253,0.22)]',
        className,
      )}
      type="button"
      whileTap={shouldReduceMotion ? undefined : { scale: 0.99 }}
      {...props}
    >
      <AnimatePresence initial={false} mode="wait">
        <motion.span
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          className="inline-flex items-center justify-center gap-2"
          exit={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : -6,
            filter: shouldReduceMotion ? 'blur(0px)' : 'blur(2px)',
          }}
          initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 6,
            filter: shouldReduceMotion ? 'blur(0px)' : 'blur(2px)',
          }}
          key={cartState}
          transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
          onAnimationComplete={() => {
            if (cartState === 'flash') onCartFlashAnimationComplete?.();
          }}
        >
          {cartState === 'flash' ? (
            <>
              <Check aria-hidden="true" className="h-4 w-4" strokeWidth={2.8} />
              Agregado
            </>
          ) : null}
          {cartState === 'pay' ? (
            <>
              {payLabel}
              <ArrowRight aria-hidden="true" className="h-4 w-4" strokeWidth={2.4} />
            </>
          ) : null}
          {cartState === 'idle' ? (
            <>
              {hideIdleIcon ? null : (
                <ShoppingCart aria-hidden="true" className="h-4 w-4" strokeWidth={2.4} />
              )}
              {idleLabel}
            </>
          ) : null}
        </motion.span>
      </AnimatePresence>
    </motion.button>
  );
}
